import React from "react";
import { Link } from "react-router-dom";
import { 
  FaShieldAlt, 
  FaLock, 
  FaUserShield, 
  FaServer, 
  FaRobot, 
  FaEnvelope, 
  FaCheckCircle,
  FaArrowRight,
  FaFileAlt
} from "react-icons/fa";
import useSEO from "./useSEO";

const Privacy = () => {
  useSEO({
    title: "Privacy Policy & Enterprise Data Protection | Core Spectators",
    description: "Read Core Spectators' privacy policy regarding client data protection, zero-AI training pledge on proprietary code, cookie policies, and GDPR/CCPA compliance.",
    keywords: "Core Spectators privacy policy, client data protection, AI data privacy, enterprise software security, GDPR compliance, CCPA rights, confidential software engineering",
    canonical: "https://corespectators.com/Privacy",
    schema: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Privacy Policy & Enterprise Data Governance",
      "url": "https://corespectators.com/Privacy",
      "description": "Core Spectators' official privacy policy governing client confidentiality, data handling, and enterprise security."
    }
  });

  return (
    <div className="bg-white min-h-screen py-10 sm:py-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="text-xs font-bold text-red-600 uppercase tracking-wider bg-red-50 px-3.5 py-1.5 rounded-full border border-red-100 inline-flex items-center gap-1.5 mb-4">
            <FaShieldAlt className="text-red-600 text-xs" /> Legal &amp; Data Governance
          </span>
          <h1 className="font-extrabold text-3xl sm:text-4xl lg:text-5xl text-gray-900 leading-tight">
            Privacy Policy &amp; <span className="text-red-600">Data Protection</span>
          </h1>
          <p className="mt-4 text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed">
            At Core Spectators, we prioritize confidentiality, robust encryption, and client data sovereignty across all our custom Shopify storefronts, mobile applications, SaaS MVPs, and AI automation systems.
          </p>
          <div className="mt-4 text-xs font-semibold text-gray-600">
            Last Updated: October 2026 • Effective Date: January 2024
          </div>
        </div>

        {/* Security Highlights Banner */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-14">
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 flex items-start gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center shrink-0">
              <FaLock className="text-lg" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900 text-sm">256-Bit TLS Encryption</h2>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                All client project communications and submissions are encrypted in transit and at rest.
              </p>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 flex items-start gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center shrink-0">
              <FaRobot className="text-lg" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900 text-sm">Zero AI Data Training</h2>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                Client proprietary codebase, schemas, and voice streams are never used to train public models.
              </p>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 flex items-start gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center shrink-0">
              <FaUserShield className="text-lg" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900 text-sm">GDPR &amp; CCPA Ready</h2>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                Full compliance with international consumer rights, right to deletion, and data portability.
              </p>
            </div>
          </div>
        </div>

        {/* Main Policy Content Sections */}
        <div className="space-y-10 text-gray-700 text-sm sm:text-base leading-relaxed">

          {/* 1. Overview & Commitment */}
          <section className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 shadow-sm">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2.5 mb-4">
              <span className="w-2 h-5 bg-red-600 rounded-full inline-block"></span>
              1. Overview &amp; Commitment to Privacy
            </h2>
            <p className="mb-3">
              Core Spectators ("we," "our," or "us") operates as a specialized full-stack software and e-commerce engineering agency. This Privacy Policy governs the collection, processing, storage, and protection of personal data and technical specifications collected through our website (<a href="https://corespectators.com" className="text-red-600 font-semibold hover:underline">corespectators.com</a>), project estimation portals, APIs, and client engagements.
            </p>
            <p>
              By accessing our platform or submitting project specifications, you acknowledge and agree to the practices outlined in this policy. If you have questions or require a bilateral Non-Disclosure Agreement (NDA) prior to sharing proprietary architectural details, contact us directly at <a href="mailto:contact@corespectators.com" className="text-red-600 font-semibold hover:underline">contact@corespectators.com</a>.
            </p>
          </section>

          {/* 2. What Information We Collect */}
          <section className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 shadow-sm">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2.5 mb-4">
              <span className="w-2 h-5 bg-red-600 rounded-full inline-block"></span>
              2. Information We Collect
            </h2>
            <p className="mb-4">
              We collect information that you voluntarily provide to us as well as technical data automatically captured during your visits:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <h3 className="font-bold text-gray-900 text-sm mb-2 flex items-center gap-2">
                  <FaFileAlt className="text-red-600" /> Information You Provide Directly
                </h3>
                <ul className="text-xs sm:text-sm space-y-1.5 text-gray-600">
                  <li>• Client name, company entity, direct email address, and phone number.</li>
                  <li>• Detailed project scope, technical requirements, and target launch dates.</li>
                  <li>• Design aesthetic preferences, competitor benchmarks, and budget allocations.</li>
                  <li>• Inbound contact inquiries and direct customer support communications.</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <h3 className="font-bold text-gray-900 text-sm mb-2 flex items-center gap-2">
                  <FaServer className="text-red-600" /> Automatically Collected Data
                </h3>
                <ul className="text-xs sm:text-sm space-y-1.5 text-gray-600">
                  <li>• Internet Protocol (IP) address, browser version, and operating system.</li>
                  <li>• Referral source URLs, visit timestamps, and clickstream interaction telemetry.</li>
                  <li>• Performance monitoring metrics, page load speeds, and error diagnostics.</li>
                  <li>• Geographic location data at country/region level.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 3. AI Data Governance & Zero-Training Pledge */}
          <section className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 shadow-sm">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2.5 mb-4">
              <span className="w-2 h-5 bg-red-600 rounded-full inline-block"></span>
              3. AI Workflows &amp; Zero-Training Data Pledge
            </h2>
            <p className="mb-3">
              As an agency developing automated AI pipelines, conversational voice agents, and custom RAG knowledge bases, we uphold strict standards of artificial intelligence governance:
            </p>
            <ul className="space-y-2.5 my-4">
              <li className="flex items-start gap-2.5">
                <FaCheckCircle className="text-green-600 text-sm mt-1 shrink-0" />
                <span>
                  <strong>Zero Public LLM Training:</strong> Proprietary client source code, database schemas, internal documentation, and training data will <em>never</em> be utilized to train or fine-tune public language models.
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <FaCheckCircle className="text-green-600 text-sm mt-1 shrink-0" />
                <span>
                  <strong>Enterprise API Guardrails:</strong> When integrating external LLM endpoints (such as OpenAI, Anthropic Claude, or Google Gemini), we utilize enterprise zero-data-retention agreements where inputs are not stored or analyzed.
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <FaCheckCircle className="text-green-600 text-sm mt-1 shrink-0" />
                <span>
                  <strong>Voice Telephony Encryption:</strong> Live bidirectional audio streams from conversational telephony agents are transmitted via secure WebRTC / TLS channels and redacted in compliance with customer privacy rules.
                </span>
              </li>
            </ul>
          </section>

          {/* 4. E-Commerce & Shopify Customer Privacy */}
          <section className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 shadow-sm">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2.5 mb-4">
              <span className="w-2 h-5 bg-red-600 rounded-full inline-block"></span>
              4. Shopify &amp; E-Commerce Data Security
            </h2>
            <p className="mb-3">
              When engineering custom Shopify 2.0 themes, headless Hydrogen storefronts, or Recharge subscription funnels, we adhere to global e-commerce security standards:
            </p>
            <p className="mb-3">
              Payment card transactions are handled exclusively by Level-1 PCI-DSS compliant gateways (Shopify Payments, Stripe, Apple Pay, Google Pay). Core Spectators never stores, inspects, or logs raw credit card credentials or sensitive CVV numbers.
            </p>
          </section>

          {/* 5. Cookies & Tracking Technologies */}
          <section className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 shadow-sm">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2.5 mb-4">
              <span className="w-2 h-5 bg-red-600 rounded-full inline-block"></span>
              5. Cookies &amp; Local Storage Policy
            </h2>
            <p className="mb-3">
              Our website uses first-party session cookies and lightweight local storage to maintain session state, optimize Core Web Vitals load times, and preserve user preferences. We do not engage in invasive third-party ad-network tracking.
            </p>
            <p>
              You may manage or disable cookies at any time through your browser settings. Essential session cookies required for security and form state preservation will remain functional.
            </p>
          </section>

          {/* 6. Third-Party Service Providers */}
          <section className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 shadow-sm">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2.5 mb-4">
              <span className="w-2 h-5 bg-red-600 rounded-full inline-block"></span>
              6. Third-Party Service Providers &amp; Infrastructure
            </h2>
            <p className="mb-3">
              We may engage vetted third-party service providers to host cloud infrastructure, provide DNS resolution, and deliver transactional notifications on our behalf:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-gray-600 text-sm">
              <li>Cloud Infrastructure &amp; Edge CDN: AWS (Amazon Web Services), Cloudflare, Vercel.</li>
              <li>Transactional Email Services: Authenticated SMTP relay via TLS encryption.</li>
              <li>Diagnostic Analytics: Privacy-friendly aggregated telemetry with anonymized IP addresses.</li>
            </ul>
          </section>

          {/* 7. Client Rights (GDPR & CCPA) */}
          <section className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 shadow-sm">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2.5 mb-4">
              <span className="w-2 h-5 bg-red-600 rounded-full inline-block"></span>
              7. Your Rights (GDPR, CCPA &amp; International Law)
            </h2>
            <p className="mb-3">
              Depending on your jurisdiction, you hold specific legal rights concerning your personal data:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-4">
              <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-200 text-xs sm:text-sm">
                <strong>• Right to Access:</strong> Request a complete copy of all data stored concerning your entity.
              </div>
              <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-200 text-xs sm:text-sm">
                <strong>• Right to Rectification:</strong> Request correction of inaccurate or incomplete contact details.
              </div>
              <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-200 text-xs sm:text-sm">
                <strong>• Right to Erasure ("To Be Forgotten"):</strong> Request immediate permanent deletion of records.
              </div>
              <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-200 text-xs sm:text-sm">
                <strong>• Right to Non-Discrimination:</strong> Equal service terms regardless of privacy rights exercised.
              </div>
            </div>
            <p className="text-xs text-gray-500">
              To exercise any of these rights, send a written request to <a href="mailto:contact@corespectators.com" className="text-red-600 font-semibold underline">contact@corespectators.com</a> with the subject "Data Subject Request". We respond within 30 business days.
            </p>
          </section>

          {/* 8. Contact Information */}
          <section className="bg-gradient-to-br from-gray-900 to-black text-white rounded-3xl p-8 sm:p-10 shadow-xl">
            <h2 className="text-2xl font-bold text-white mb-2">
              Questions Regarding Our Privacy Governance?
            </h2>
            <p className="text-gray-300 text-sm sm:text-base mb-6 leading-relaxed">
              Our engineering leadership and data protection officers are available to address compliance inquiries, sign project NDAs, or provide architectural security assessments.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <a 
                href="mailto:contact@corespectators.com"
                className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-3 rounded-xl transition shadow flex items-center gap-2 text-sm sm:text-base"
              >
                <FaEnvelope /> Email Privacy Team
              </a>
              <Link 
                to="/Form"
                className="bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-xl border border-white/20 transition flex items-center gap-2 text-sm sm:text-base"
              >
                Submit Project NDA &amp; Scope <FaArrowRight className="text-xs" />
              </Link>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default Privacy;
